import * as React from 'react';
import { FC, useRef } from 'react';

import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { IStore } from '@rbi-ctg/store';
import RedeemRewardActionButton from 'components/cart-item/redeem-reward/redeem-reward-action';
import { RewardActions } from 'components/cart-item/redeem-reward/types';
import {
  makeLoyaltyRewardDetailsPath,
  makeUrlForIncentive,
} from 'pages/loyalty/loyalty-incentives-components/incentive-details/utils';
import { useLogger } from 'state/logger';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { useMenuContext } from 'state/menu';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { maybeLocaleImage } from 'utils/graphql';
import noop from 'utils/noop';

import {
  BtnContainer,
  CardContent,
  Container,
  ExclamationIcon,
  ImageContainer,
  NotAvailableText,
  ProductImg,
  RewardName,
} from './rewards-carousel-card.styled';
import { IRewardsCarouselCardProps } from './types';

export const RewardsCarouselCard: FC<IRewardsCarouselCardProps> = ({ reward }) => {
  const image = maybeLocaleImage(reward.image);
  const name = reward?.name?.locale || '';
  const { noStoreSelected, store } = useStoreContext();
  const { setSelectedStaticMenuItem } = useMenuContext();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const logger = useLogger();
  const incentiveRef = useRef(reward.incentives?.find(val => val?._id === reward.rewardBenefitId));
  const { serviceMode } = useServiceModeContext();
  const { isInRestaurantRedemptionEnabledAtStore } = useInRestaurantRedemptionContext();
  const { logRBIEvent } = useMParticleContext();

  const handleRewardClick = (action: RewardActions) => {
    if (action !== RewardActions.ADD) {
      return noop;
    }
    return async () => {
      const rewardWizardUrl = makeUrlForIncentive(reward);
      if (!rewardWizardUrl) {
        logger.warn(
          `Attempting to make a url for reward that is missing data: reward ${reward._id}`
        );
        return;
      }
      const indexOfSearch = rewardWizardUrl.indexOf('?');
      const search = indexOfSearch !== -1 ? rewardWizardUrl.slice(indexOfSearch) : '';
      if (incentiveRef.current && noStoreSelected) {
        setSelectedStaticMenuItem({
          itemId: `${incentiveRef.current._type}-${incentiveRef.current._id}`,
          itemName: reward?.name?.locale || '',
          search,
        });
      }

      const shouldNavigateToRewardsDetails =
        reward?._id && (await isInRestaurantRedemptionEnabledAtStore(store as IStore, serviceMode));

      if (shouldNavigateToRewardsDetails) {
        navigate(makeLoyaltyRewardDetailsPath({ rewardId: reward._id, search, formatMessage }));
        logRBIEvent({
          name: CustomEventNames.REWARD_SELECTED,
          type: EventTypes.Other,
          attributes: {
            engineId: reward.loyaltyEngineId ?? '',
            sanityId: reward._id ?? '',
            name: reward.name?.locale ?? '',
          },
        });
      } else {
        navigate(rewardWizardUrl);
      }
    };
  };

  return (
    <Container>
      <ImageContainer>
        {image && <ProductImg image={image} alt={name} objectFitContain />}
      </ImageContainer>
      <CardContent>
        <RewardName>{name}</RewardName>
        <BtnContainer>
          {reward.isAvailable && (
            <RedeemRewardActionButton
              points={reward.pointCost}
              handleRewardClick={handleRewardClick}
              action={RewardActions.ADD}
              disabled={reward.locked}
              locked={reward.locked}
            />
          )}
          {!reward.isAvailabilityLoading && !reward.isAvailable && (
            <NotAvailableText>
              <ExclamationIcon>!</ExclamationIcon>
              <span>{formatMessage({ id: 'notAvailableAtStore' })}</span>
            </NotAvailableText>
          )}
        </BtnContainer>
      </CardContent>
    </Container>
  );
};
